/** High-level writes: enqueue in the outbox → optimistic cache update → try to sync. */
import type { AscentInput, BucketList, CustomPeakInput, ExternalResult, ListInput, ListItemInput, Peak, ProfileInput } from "../../shared/schemas";
import type { InfiniteData } from "@tanstack/react-query";
import type {
  AscentDetail,
  ChallengesPage,
  Comments,
  FeedItem,
  FollowRow,
  Invite,
  MyGroups,
  NoticesPage,
  Page,
  PersonCard,
  PersonRow,
  PushCategory,
  PushSettings,
  ReportInput,
  SocialAscent,
  SocialMe,
  SocialMeInput,
} from "../../shared/social";
import { cleanUserText } from "../../shared/text";
import { api } from "./api";
import { putBlob } from "./idb";
import type { PreparedPhoto } from "./photo";
import {
  applyToAscentDetail,
  applyToAscents,
  applyToBlocks,
  applyToChallenges,
  applyToComments,
  applyToExtraPeaks,
  applyToFeed,
  applyToFollowers,
  applyToFollowing,
  applyToGroups,
  applyToInvites,
  applyToLists,
  applyToNotices,
  applyToNoticesCount,
  applyToPerson,
  applyToPersonAscents,
  applyToPins,
  applyToProfile,
  applyToPush,
  applyToRequests,
  applyToSocialMe,
  isSocialOp,
  type ClientAscent,
  type ClientPersonCard,
  type ClientProfile,
  type Op,
} from "./ops";
import { enqueue } from "./outbox";
import { keys, queryClient } from "./queries";
import { scheduleFlush } from "./sync";

async function run(op: Op) {
  await enqueue(op);
  queryClient.setQueryData<ClientAscent[]>(keys.ascents, (d) => (d ? applyToAscents(d, op) : d));
  queryClient.setQueryData<BucketList[]>(keys.lists, (d) => (d ? applyToLists(d, op) : d));
  queryClient.setQueryData<Peak[]>(keys.extraPeaks, (d) => (d ? applyToExtraPeaks(d, op) : d));
  // Seeded even when not loaded yet: pinning is its own full state, so a cold offline start shows it.
  if (op.type === "pins.set") queryClient.setQueryData<string[]>(keys.pins, (d) => applyToPins(d ?? [], op));
  if (op.type.startsWith("avatar.") || op.type === "profile.put") {
    queryClient.setQueryData<ClientProfile>(keys.profile, (d) => applyToProfile(d ?? { userId: null, avatarUrl: null }, op));
  }
  // Deleting one's ascent forgets its group too (a social cache, for a non-social op).
  if (isSocialOp(op) || op.type === "ascent.delete") applyToSocialCaches(op);
  scheduleFlush();
}

/** The same reducers the refetch overlay uses (lib/social.ts), on every cached social list and card. */
function applyToSocialCaches(op: Op) {
  queryClient.setQueryData<SocialMe>(keys.socialMe, (d) => (d ? applyToSocialMe(d, op) : d));
  for (const [key, card] of queryClient.getQueriesData<ClientPersonCard | null>({ queryKey: ["person"] })) {
    if (key.length === 2 && card) queryClient.setQueryData(key, applyToPerson(card, op));
  }
  queryClient.setQueryData<FollowRow[]>(keys.following, (d) => (d ? applyToFollowing(d, op) : d));
  queryClient.setQueryData<FollowRow[]>(keys.followers, (d) => (d ? applyToFollowers(d, op) : d));
  queryClient.setQueryData<FollowRow[]>(keys.requests, (d) => (d ? applyToRequests(d, op) : d));
  queryClient.setQueryData<PersonRow[]>(keys.blocks, (d) => (d ? applyToBlocks(d, op) : d));
  queryClient.setQueryData<NoticesPage>(keys.notices, (d) => (d ? applyToNotices(d, op) : d));
  queryClient.setQueryData<{ unread: number }>(keys.noticesCount, (d) => (d ? applyToNoticesCount(d, op) : d));
  queryClient.setQueryData<PushSettings>(keys.push, (d) => (d ? applyToPush(d, op) : d));
  queryClient.setQueryData<Invite[]>(keys.invites, (d) => (d ? applyToInvites(d, op) : d));
  queryClient.setQueryData<MyGroups>(keys.groups, (d) => (d ? applyToGroups(d, op) : d));
  queryClient.setQueryData<InfiniteData<Page<FeedItem>>>(keys.feed, (d) => (d ? { ...d, pages: d.pages.map((p) => applyToFeed(p, op)) } : d));
  for (const [key, page] of queryClient.getQueriesData<Page<SocialAscent> | null>({ queryKey: ["person"] })) {
    if (key.length === 3 && key[2] === "ascents" && page) queryClient.setQueryData(key, applyToPersonAscents(page, key[1] as string, op));
  }
  for (const [key, d] of queryClient.getQueriesData<AscentDetail | null>({ queryKey: ["person"] })) {
    if (key.length === 4 && key[2] === "ascent" && d) queryClient.setQueryData(key, applyToAscentDetail(d, op));
  }
  for (const [key, c] of queryClient.getQueriesData<Comments | null>({ queryKey: ["comments"] })) {
    if (c) queryClient.setQueryData(key, applyToComments(c, key[1] as string, key[2] as string, op));
  }
  for (const [key, page] of queryClient.getQueriesData<ChallengesPage>({ queryKey: ["challenges"] })) {
    if (page?.items) queryClient.setQueryData(key, applyToChallenges(page, op));
  }
}

export const uuid = () => crypto.randomUUID();

export async function saveAscent(id: string, data: AscentInput) {
  // Make sure the cache exists so the optimistic update shows even on a cold offline start.
  if (!queryClient.getQueryData(keys.ascents)) queryClient.setQueryData(keys.ascents, []);
  await run({ type: "ascent.put", id, data });
}

export const deleteAscent = (id: string) => run({ type: "ascent.delete", id });

export async function setGpx(ascentId: string, bytes: ArrayBuffer) {
  const blobKey = `gpx:${ascentId}:${uuid()}`;
  await putBlob(blobKey, { buf: bytes, type: "application/gpx+xml" });
  await run({ type: "gpx.put", ascentId, blobKey });
}
export const deleteGpx = (ascentId: string) => run({ type: "gpx.delete", ascentId });

/**
 * An ascent's photo, already prepared (lib/photo.ts): kept in IndexedDB and shown from there until the
 * outbox has uploaded it; the id makes the upload idempotent. At the cap it replaces the current one.
 */
export async function setAscentPhoto(ascentId: string, photo: PreparedPhoto) {
  const photoId = uuid();
  const blobKey = `photo:${ascentId}:${photoId}`;
  await putBlob(blobKey, photo.blob);
  await run({ type: "ascentPhoto.put", ascentId, photoId, blobKey, w: photo.w, h: photo.h });
}
export const removeAscentPhoto = (ascentId: string, photoId: string) => run({ type: "ascentPhoto.delete", ascentId, photoId });

export const saveList = (id: string, data: ListInput) => run({ type: "list.put", id, data });
export const deleteList = (id: string) => run({ type: "list.delete", id });
export const reorderLists = (ids: string[]) => run({ type: "list.reorder", ids });
export const saveListItem = (listId: string, peakId: string, data: ListItemInput) =>
  run({ type: "listItem.put", listId, peakId, data });
export const removeListItem = (listId: string, peakId: string) => run({ type: "listItem.delete", listId, peakId });
export const addListItems = (listId: string, peakIds: string[]) =>
  peakIds.length ? run({ type: "listItems.add", listId, peakIds }) : Promise.resolve();

/** Replace the profile's pinned stamps (ordered, at most MAX_PINS). */
export const setPins = (peakIds: string[]) => run({ type: "pins.set", peakIds });

/**
 * A new profile picture, already cropped and resized by `lib/avatar.ts`. Kept in IndexedDB and
 * shown from there until the outbox has uploaded it; the id makes the upload idempotent.
 */
export async function setAvatar(image: Blob) {
  const avatarId = uuid();
  const blobKey = `avatar:${avatarId}`;
  await putBlob(blobKey, image);
  await run({ type: "avatar.put", avatarId, blobKey });
}
export const removeAvatar = () => run({ type: "avatar.delete" });

/** Name, birth year, sex and home area (Ajustes → Perfil); works offline like every other change. */
export const saveProfile = (data: ProfileInput) => run({ type: "profile.put", data });

// ── Social: through the outbox like everything else; keyed by the other person's id ─────────────────

const rowOf = (p: PersonRow): PersonRow => ({ id: p.id, handle: p.handle, name: p.name, avatarUrl: p.avatarUrl });

/** The whole social profile (PUT /api/social/me). Claiming a new handle needs a connection (its check). */
export const saveSocial = (data: SocialMeInput) => run({ type: "social.put", data });
export const follow = (card: PersonCard) => run({ type: "follow.put", person: rowOf(card), approval: card.approval });
export const unfollow = (userId: string) => run({ type: "follow.delete", userId });
export const approveFollower = (person: PersonRow) => run({ type: "follower.approve", person: rowOf(person) });
export const removeFollower = (userId: string) => run({ type: "follower.remove", userId });
export const block = (person: PersonRow) => run({ type: "block.put", person: rowOf(person) });
export const unblock = (userId: string) => run({ type: "block.delete", userId });
export const report = (data: ReportInput) => run({ type: "report.put", id: uuid(), data });
export const markNoticesRead = (until: string) => run({ type: "notices.read", until });
/** A piedra on someone's ascent, or taking it back; `me` shows at once among who put one. */
export const putPiedra = (ownerId: string, ascentId: string, me?: PersonRow | null) => run({ type: "piedra.put", ownerId, ascentId, ...(me ? { by: rowOf(me) } : {}) });
export const removePiedra = (ownerId: string, ascentId: string, me?: PersonRow | null) => run({ type: "piedra.delete", ownerId, ascentId, ...(me ? { by: me.id } : {}) });

/** A comment, created once by its client id; `author` is the writer as others see them (shown at once). */
export function sendComment(ownerId: string, ascentId: string, text: string, author: PersonRow) {
  return run({ type: "comment.put", ownerId, ascentId, id: uuid(), body: cleanUserText(text), author, createdAt: new Date().toISOString() });
}
export const deleteComment = (ownerId: string, ascentId: string, id: string) => run({ type: "comment.delete", ownerId, ascentId, id });

/** What is muted on the phone, on every device. */
export const setPushMute = (mute: PushCategory[]) => run({ type: "push.prefs", mute });

// ── Group ascents (docs/archive/PLAN-GROUPS.md §2) ────────────────────────────────────────────────────────

/** Invite someone to one of one's ascents (after the ascent itself, in the outbox's order). */
export const inviteToAscent = (ascentId: string, person: PersonRow) => run({ type: "invite.put", ascentId, person: rowOf(person) });
/** Withdraw an invitation still waiting. */
export const withdrawInvite = (ascentId: string, userId: string) => run({ type: "invite.delete", ascentId, userId });
/**
 * Accept an invitation: the ascent becomes one's own too, at once (offline as well). `id` is the copy's,
 * or one's own ascent of that day, which joins as it is.
 */
export const acceptInvite = (invite: Invite, id: string) => {
  if (!queryClient.getQueryData(keys.ascents)) queryClient.setQueryData(keys.ascents, []);
  return run({ type: "invite.accept", invite, id });
};
/** Decline it, silently. */
export const declineInvite = (invite: Pick<Invite, "owner" | "ascentId">) => run({ type: "invite.decline", ownerId: invite.owner.id, ascentId: invite.ascentId });

/** Join one of this month's retos, or leave it. */
export const joinChallenge = (id: string) => run({ type: "challenge.join", id });
export const leaveChallenge = (id: string) => run({ type: "challenge.leave", id });

/** Pause the streak for these weeks (labels), or lift a pause. */
export async function pauseWeeks(weeks: string[]) {
  for (const week of weeks) await run({ type: "pause.put", week });
}
export async function unpauseWeeks(weeks: string[]) {
  for (const week of weeks) await run({ type: "pause.delete", week });
}

export async function saveCustomPeak(data: CustomPeakInput, id = `c-${uuid()}`) {
  if (!queryClient.getQueryData(keys.extraPeaks)) queryClient.setQueryData(keys.extraPeaks, []);
  await run({ type: "peak.put", id, data });
  return id;
}

/** Live search results are online-only by nature, so they are imported directly (dedupe on the server). */
export function importExternalPeak(r: ExternalResult): Promise<Peak> {
  return importPeak({
    name: r.name,
    lat: r.lat,
    lon: r.lon,
    elevation: r.elevation,
    osmType: r.osmType,
    osmId: r.osmId,
    wikidataId: r.wikidataId,
    country: r.country,
    localName: r.localName,
    source: r.source,
  });
}

/** One of the world's summits the map draws (`map/world.ts`): the server reads it from Wikidata by its id. */
export const importWikidataPeak = (wikidataId: string) => importPeak({ wikidataId });

async function importPeak(body: Record<string, unknown>): Promise<Peak> {
  const { peak } = await api.post<{ peak: Peak; created: boolean }>("/api/peaks/import", body);
  if (!peak.inCatalog) {
    queryClient.setQueryData<Peak[]>(keys.extraPeaks, (d) => [...(d ?? []).filter((p) => p.id !== peak.id), peak]);
  }
  return peak;
}
